import axios, { InternalAxiosRequestConfig } from "axios";
import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

import { getCookie, setCookie } from "../utils/utilsCookie";
import { MethodType, ResponseInterface } from "./type";
import { cognitoApp, parseJwt, redirectToLearner } from "@utils/utilsCognito";
import {
  REFRESH_TOKEN_EXPIRED,
  isSessionExpiredTriggered,
  triggerSessionExpired,
} from "@utils/utilsSessionExpiredBus";

//instance creation
export const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL}`,
  headers: {
    "content-type": "application/json",
  },
});

export async function fetcher<T = any>(
  url: string,
  method: MethodType,
  body?: T,
  queryParameters?: { [x: string]: any },
) {
  const res = await customApi(url, method, body, queryParameters);
  return res.data;
}

export function customApi<T = any>(
  url: string,
  method: MethodType,
  body?: any,
  queryParameters?: { [x: string]: any },
  config?: InternalAxiosRequestConfig<any>,
): Promise<ResponseInterface<T>> {
  switch (method) {
    case MethodType.POST: {
      return axiosInstance.post(url, body, config);
    }
    case MethodType.PUT: {
      return axiosInstance.put(url, body, config);
    }
    case MethodType.PATCH: {
      return axiosInstance.patch(url, body, config);
    }
    case MethodType.DELETE: {
      return axiosInstance.delete(url, { params: body });
    }
    case MethodType.GET:
    default: {
      return axiosInstance.get(url, {
        ...config,
        ...{ params: queryParameters },
      });
    }
  }
}

const isRefreshTokenExpired = (error: any): boolean => {
  if (error?.code === "NotAuthorizedException") return true;
  return (
    error?.response?.status === 400 &&
    error?.response?.data?.error === "invalid_grant"
  );
};

const redirectToInitiativeLogin = (message?: string, reason?: string) => {
  const initiativeUrlFromCookie = getCookie("initiativeUrl") || "";
  message && console.error(message);

  if (initiativeUrlFromCookie) {
    const initiativeBaseUrl = initiativeUrlFromCookie.startsWith("http")
      ? initiativeUrlFromCookie
      : `https://${initiativeUrlFromCookie}`;
    const loginUrl = `${initiativeBaseUrl.replace(/\/+$/, "")}/access/login`;
    if (reason === REFRESH_TOKEN_EXPIRED) {
      if (triggerSessionExpired({ loginUrl })) return;
    } else if (isSessionExpiredTriggered()) {
      // A session-expired modal is already being shown for a previous error
      // in this same failure chain: don't race it with a silent redirect.
      return;
    }
    window.location.href = loginUrl;
    return;
  }

  redirectToLearner(
    message || "Refresh failed and initiative login URL is not available",
    reason,
  );
};

/*** REFRESH ***/
export async function refreshSessionCognito(): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    const cognitoUser = cognitoApp.getCognitoCurrentUser();
    if (cognitoUser) {
      cognitoUser.getSession((err: any, session: CognitoUserSession) => {
        if (err) {
          reject(err);
          // getSession() internally calls refreshSession() with this same
          // callback when the cached session is invalid, so an expired
          // refresh token is most commonly reported here, not below.
          redirectToInitiativeLogin(
            "An error occured during refreshSessionCognito",
            isRefreshTokenExpired(err) ? REFRESH_TOKEN_EXPIRED : undefined,
          );
          return;
        }
        cognitoUser?.refreshSession(
          session.getRefreshToken(),
          (error: any, session: CognitoUserSession) => {
            if (error) {
              reject(error);
              redirectToInitiativeLogin(
                "An error occured during refreshSessionCognito",
                isRefreshTokenExpired(error)
                  ? REFRESH_TOKEN_EXPIRED
                  : undefined,
              );
              return;
            } else if (session) {
              //Refresh token success
              resolve(session);
            } else {
              console.error("refresh cognito session: unknown error");
              reject(new Error("refresh cognito session: unknown error"));
              redirectToInitiativeLogin(
                "An error occured during refreshSessionCognito",
              );
              return;
            }
          },
        );
      });
    } else {
      const missingUserError = new Error("cognito user is not set");
      reject(missingUserError);
      redirectToInitiativeLogin(
        "An error occured during refreshSessionCognito",
      );
      return;
    }
  });
}

export async function refreshSessionSSO() {
  const userpoolId = cognitoApp.getUserpool()?.getUserPoolId();
  const { data: userPoolInfo } = await customApi<any>(
    `https://cognito-idp.eu-south-1.amazonaws.com/${userpoolId}/.well-known/openid-configuration`,
    MethodType.GET,
  );
  return new Promise((resolve, reject) => {
    const cognitoUser = cognitoApp.getCognitoCurrentUser();
    if (!cognitoUser) {
      reject(new Error("cognito user is not set"));
      redirectToInitiativeLogin("An error occured during refreshSessionSSO");
      return;
    }
    cognitoUser.getSession(async (error: any, session: CognitoUserSession) => {
      if (error) {
        reject(error);
        // getSession() internally calls refreshSession() with this same
        // callback when the cached session is invalid, so an expired
        // refresh token is most commonly reported here.
        redirectToInitiativeLogin(
          "An error occured during refreshSessionSSO",
          isRefreshTokenExpired(error) ? REFRESH_TOKEN_EXPIRED : undefined,
        );
        return;
      }
      try {
        const { data } = await axios.create().post(
          userPoolInfo.token_endpoint,
          {
            client_id: cognitoApp.getUserpool()?.getClientId(),
            grant_type: "refresh_token",
            refresh_token: session.getRefreshToken().getToken(),
          },
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          },
        );
        if (!data?.access_token || !data?.id_token) {
          const missingTokenError = new Error(
            "refreshSessionSSO: token_endpoint response is missing access_token or id_token",
          );
          console.error(missingTokenError);
          reject(missingTokenError);
          redirectToInitiativeLogin(
            "An error occured during refreshSessionSSO",
          );
          return;
        }
        // Persiste i token rinnovati nello storage del cognito user (cookie):
        // senza questo la richiesta ritentata rileggerebbe il token scaduto dai
        // cookie generando un loop di 401. token_endpoint con
        // grant_type=refresh_token di norma NON restituisce un nuovo
        // refresh_token: fallback a quello corrente.
        const refreshedSession = new CognitoUserSession({
          AccessToken: new CognitoAccessToken({
            AccessToken: data.access_token,
          }),
          IdToken: new CognitoIdToken({ IdToken: data.id_token }),
          RefreshToken: new CognitoRefreshToken({
            RefreshToken:
              data.refresh_token || session.getRefreshToken().getToken(),
          }),
        });
        cognitoUser.setSignInUserSession(refreshedSession);
        resolve(data);
      } catch (err) {
        console.error(err);
        reject(err);
        redirectToInitiativeLogin(
          "An error occured during refreshSessionSSO",
          isRefreshTokenExpired(err) ? REFRESH_TOKEN_EXPIRED : undefined,
        );
        return;
      }
    });
  });
}

const refreshToken = async () => {
  const cognitoUser = cognitoApp.getCognitoCurrentUser();
  if (cognitoUser) {
    return new Promise((resolve, reject) => {
      cognitoUser.getSession(async (err: any, session: CognitoUserSession) => {
        if (err) {
          reject(err);
          // getSession() internally calls refreshSession() with this same
          // callback when the cached session is invalid, so an expired
          // refresh token is most commonly reported here.
          redirectToInitiativeLogin(
            "An error occured during refreshSessionCognito",
            isRefreshTokenExpired(err) ? REFRESH_TOKEN_EXPIRED : undefined,
          );
          return;
        }
        const tokenParse = parseJwt(session.getIdToken().getJwtToken());
        if (
          Object.keys(tokenParse).findIndex((el) => el === "identities") >= 0
        ) {
          resolve(refreshSessionSSO());
        } else {
          resolve(refreshSessionCognito());
        }
      });
    });
  } else {
    const missingUserError = new Error(
      "It was not possible to refreshToken because cognitoUser is not present",
    );
    redirectToInitiativeLogin(
      "It was not possible to refreshToken because cognitoUser is not present",
    );
    return Promise.reject(missingUserError);
  }
};

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retryCount?: number;
};

let refreshTokenPromise: Promise<unknown> | null = null;
let refreshSessionIdPromise: Promise<string> | null = null;
let sessionPromise: Promise<CognitoUserSession> | null = null;

const sharedRefreshToken = (): Promise<unknown> => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = refreshToken().finally(() => {
      refreshTokenPromise = null;
    });
  }
  return refreshTokenPromise;
};

const getCognitoSession = (): Promise<CognitoUserSession> =>
  new Promise((resolve, reject) => {
    const cognitoUser = cognitoApp.getCognitoCurrentUser();
    if (!cognitoUser) {
      reject(new Error("cognito user is not set"));
      return;
    }
    cognitoUser.getSession((err: any, session: CognitoUserSession) => {
      if (err) reject(err);
      else resolve(session);
    });
  });

export const sharedRefreshSessionId = (): Promise<string> => {
  if (!refreshSessionIdPromise) {
    refreshSessionIdPromise = (async () => {
      const session = await getCognitoSession();
      const idToken = session.getIdToken().getJwtToken();
      const authToken = session.getAccessToken().getJwtToken();
      const tokenRefresh = session.getRefreshToken().getToken();
      if (!idToken || !authToken || !tokenRefresh) {
        throw new Error(
          "idToken, authToken or refreshToken is missing in cognito user",
        );
      }
      const sessionsPostResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}sessions`,
        {
          method: "POST",
          cache: "no-cache",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: idToken,
            AuthToken: authToken,
            RefreshToken: tokenRefresh,
          },
        },
      );
      const sessionsPostData = await sessionsPostResponse.json();
      if (!sessionsPostData.sessionId) {
        throw new Error("response of POST /sessions didn't retrieve sessionId");
      }
      setCookie("sessionId", sessionsPostData.sessionId, new Date().getTime());
      return sessionsPostData.sessionId as string;
    })().finally(() => {
      refreshSessionIdPromise = null;
    });
  }
  return refreshSessionIdPromise;
};

// Single-flight su getSession: evita N refresh paralleli quando piu' richieste
// trovano il token scaduto contemporaneamente.
const sharedGetSession = (): Promise<CognitoUserSession> => {
  if (!sessionPromise) {
    sessionPromise = getCognitoSession().finally(() => {
      sessionPromise = null;
    });
  }
  return sessionPromise;
};

axiosInstance.interceptors.request.use(async (config) => {
  if (process.env.REACT_APP_BASE_URL && !config.url?.includes("https://")) {
    // if(config.url?.includes("/skills")){
    //   config.baseURL = "https://web-learning-professions-and-skills-v1.openlearning-svil.digitedacademy.net/";
    // }
    const sessionId = getCookie("sessionId") || "";
    let authToken = getCookie("authToken") || "";
    // console.info("***authToken***", authToken);
    // console.info("***config***", config);
    const cognitoUser = cognitoApp.getCognitoCurrentUser();
    if (cognitoUser) {
      try {
        const session = await sharedGetSession();
        authToken = session.getAccessToken().getJwtToken();
      } catch (err) {
        // Fallback al token da cookie; un eventuale 401 lo gestisce il response interceptor.
      }
    }
    // Don't pass Authorization header to GET /cognito/
    if (authToken && !config.url?.includes("/cognito/")) {
      config.headers["authorization"] = authToken;
    }
    if (sessionId) {
      config.headers["x-ada-session-token"] = sessionId;
    }
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: any) => {
    const config = error?.config as RetryableRequestConfig | undefined;
    const requestUrl =
      config?.url ||
      error?.response?.config?.url ||
      error?.request?.responseURL ||
      "";
    const statusCode = error?.response?.status || error?.toJSON?.().status;

    if (statusCode !== 401) {
      throw error;
    }

    // Let callers handle /cognito/ 401 explicitly (e.g. initiative login redirect).
    if (requestUrl.includes("/cognito/")) {
      return Promise.reject(error);
    }

    if (!config) {
      throw error;
    }

    config._retryCount = (config._retryCount ?? 0) + 1;

    if (config._retryCount > 2) {
      redirectToLearner(
        "API call status again for 2 times in 401 after trying to refresh token or session",
      );
      return Promise.reject(error);
    }

    try {
      // First retry: refresh the Cognito token. Second retry: only refresh
      // the backend sessionId, the token has already been rotated.
      if (config._retryCount === 1) {
        await sharedRefreshToken();
      }
      await sharedRefreshSessionId();
    } catch (err: any) {
      redirectToInitiativeLogin(
        typeof err?.message === "string" ? err.message : err,
      );
      return Promise.reject(error);
    }

    return axiosInstance.request(config);
  },
);
